import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link } from "react-router-dom";
import { useState } from "react";

const useStyles = makeStyles({
  root: {
    width: "100%",
    maxWidth: 420,
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
  },
  loginTitle: {
    margin: "0 0 28px",
    textAlign: "center",
    fontWeight: 600,
    fontSize: "1.35rem",
    color: "#050821",
  },
  form: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: 18,
  },
  fieldGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  label: {
    fontSize: "0.95rem",
    fontWeight: 500,
    color: "#1A1A1A",
  },
  textField: {
    width: "100%",
    backgroundColor: "#fff",
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      backgroundColor: "#fff",
      minHeight: 48,
      "& fieldset": {
        borderColor: "#C9C9C9",
      },
      "&:hover fieldset": {
        borderColor: "#9E9E9E",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#050821",
        borderWidth: 1,
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "12px 14px !important",
      height: "24px !important",
      boxSizing: "content-box",
      fontSize: "1rem !important",
    },
  },
  optionsRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginTop: 2,
  },
  checkboxLabel: {
    margin: 0,
    "& .MuiFormControlLabel-label": {
      fontSize: "0.9rem",
      color: "#1A1A1A",
    },
    "& .MuiCheckbox-root": {
      color: "#050821",
      padding: "4px 8px 4px 0",
    },
    "& .Mui-checked": {
      color: "#050821 !important",
    },
  },
  forgotLink: {
    color: "#050821",
    textDecoration: "none",
    fontSize: "0.9rem",
    whiteSpace: "nowrap",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  submitButton: {
    marginTop: 8,
    height: 48,
    width: "100%",
    backgroundColor: "#050821 !important",
    color: "#fff !important",
    textTransform: "none !important",
    fontWeight: "700 !important",
    fontSize: "1.05rem !important",
    borderRadius: "10px !important",
    boxShadow: "none !important",
    border: "1px solid #050821",
    "&:hover": {
      backgroundColor: "#121531 !important",
      boxShadow: "none !important",
    },
  },
  footer: {
    marginTop: 28,
    textAlign: "center",
    fontSize: "0.9rem",
    color: "#1A1A1A",
  },
  registerLink: {
    color: "#050821",
    fontWeight: 700,
    textDecoration: "underline",
    "&:hover": {
      filter: "brightness(1.1)",
    },
  },
  errorText: {
    fontSize: "0.8rem",
    color: "#d32f2f",
    marginTop: 2,
  },
});

const formSchema = yup.object().shape({
  username: yup
    .string()
    .required("E-mail obrigatório")
    .email("E-mail inválido"),
  password: yup.string().required("Senha obrigatória"),
});

export const LoginForm = ({
  email,
  password,
  handleLogin,
  setPassword,
  setEmail,
  setForgotPassword,
}) => {
  const {
    register,
    handleSubmit,
    setFocus,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(formSchema),
  });

  const [showPassword, setShowPassword] = useState(false);
  const classes = useStyles();

  const handleChangePassword = (e) => {
    setFocus("password");
    setPassword(e.target.value);
  };

  const handleChangeEmail = (e) => {
    setFocus("username");
    setEmail(e.target.value);
  };

  const handleEnterClick = (event, inputType) => {
    if (event.keyCode === 13) {
      if (inputType === "password") {
        setFocus("username");
        setFocus("password");
      } else {
        setFocus("password");
        setFocus("username");
      }

      handleSubmit(handleLogin);
    }
  };

  return (
    <div className={classes.root}>
      <h2 className={classes.loginTitle}>Login</h2>

      <form className={classes.form} onSubmit={handleSubmit(handleLogin)}>
        <input type="hidden" value="something" />

        <Box className={classes.fieldGroup}>
          <Typography component="label" htmlFor="outlined-email-input" className={classes.label}>
            Email
          </Typography>
          <TextField
            id="outlined-email-input"
            className={classes.textField}
            type="text"
            placeholder="usuario@gmail.com"
            value={email.value}
            onKeyDown={(e) => handleEnterClick(e, "email")}
            {...register("username")}
            error={!!errors.username}
            autoComplete="current-email"
            onChange={(e) => handleChangeEmail(e)}
          />
          {errors.username?.message ? (
            <Typography className={classes.errorText}>
              {errors.username.message}
            </Typography>
          ) : null}
        </Box>

        <Box className={classes.fieldGroup}>
          <Typography
            component="label"
            htmlFor="outlined-password-input"
            className={classes.label}
          >
            Senha
          </Typography>
          <TextField
            id="outlined-password-input"
            className={classes.textField}
            type={showPassword ? "text" : "password"}
            placeholder="************"
            value={password.value}
            onKeyDown={(e) => handleEnterClick(e, "password")}
            {...register("password")}
            error={!!errors.password}
            autoComplete="current-password"
            onChange={(e) => handleChangePassword(e)}
          />
          {errors.password?.message ? (
            <Typography className={classes.errorText}>
              {errors.password.message}
            </Typography>
          ) : null}
        </Box>

        <Box className={classes.optionsRow}>
          <FormControlLabel
            className={classes.checkboxLabel}
            control={
              <Checkbox
                checked={showPassword}
                onChange={(e) => setShowPassword(e.target.checked)}
                size="small"
              />
            }
            label="Exibir senha"
          />
          <Link
            onClick={() => setForgotPassword(true)}
            to=""
            className={classes.forgotLink}
          >
            Esqueci minha senha
          </Link>
        </Box>

        <Button
          type="submit"
          variant="contained"
          disableElevation
          className={classes.submitButton}
          sx={{
            backgroundColor: "#050821 !important",
            color: "#fff !important",
            borderRadius: "10px !important",
            fontWeight: "700 !important",
            textTransform: "none !important",
            "&:hover": {
              backgroundColor: "#121531 !important",
            },
          }}
        >
          Entrar
        </Button>

        <Typography className={classes.footer}>
          Ainda não tem uma conta?{" "}
          <Link to="/register" className={classes.registerLink}>
            Registre-se.
          </Link>
        </Typography>
      </form>
    </div>
  );
};
